import React, { useEffect, useState } from 'react';

// Helper function to get the file extension from the URL
const getExt = (url = "") => url.split("?")[0].split("#")[0].split(".").pop().toLowerCase();

const FileViewer = ({ sourceUrl, pageNumber, highlightText, highlightBoxes, resetScrollKey }) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber?.[0]?.page_number || 1); // Default to 1 if no page number
  const iframeRef = React.useRef(null);
  const [highlightedPage, setHighlightedPage] = useState(null); // Store the page to be highlighted
  const ext = getExt(sourceUrl); // Get the file extension to determine which viewer to use

  // Log the current source values when they change
  useEffect(() => {
    console.log("Updated Page Number:", currentPageNumber);  // Log updated page number
  }, [currentPageNumber]);

  // Generate the viewer URL for the PDF based on the page number
  const getViewerUrl = () => {
    const url = `${sourceUrl}#page=${currentPageNumber}`;
    console.log("Generated Viewer URL:", url);  // Log the final URL being generated
    return url; // Open the page in a PDF viewer
  };

  // Apply the new page number when the component updates
  useEffect(() => {
    if (Array.isArray(pageNumber) && pageNumber.length > 0) {
      const newPageNumber = pageNumber[0]?.page_number || 1; // Ensure we get a valid page number
      if (newPageNumber !== currentPageNumber) {
        setCurrentPageNumber(newPageNumber); // Update the page number when source changes
        console.log("Page number updated to:", newPageNumber);  // Log when page number is updated
      }
    }
  }, [pageNumber, resetScrollKey]);

  // Update the highlighted page when highlightText or highlightBoxes changes
  useEffect(() => {
    if (highlightText || highlightBoxes) {
      console.log("Highlight triggered. Setting highlighted page to:", currentPageNumber);
      setHighlightedPage(currentPageNumber);  // Highlight the current page when these change
    }
  }, [highlightText, highlightBoxes, currentPageNumber]);

  const handleIframeLoad = () => {
    // This function is called when the iframe is loaded
    console.log('Iframe loaded successfully!');
  };

  const getHighlightStyles = () => {
    // Calculate the positioning and size for the highlight box
    const pageHeight = 1000; // Modify with actual page height logic
    const pageWidth = 800; // Modify with actual page width logic

    console.log("Highlight styles calculated for page:", currentPageNumber);
    return {
      position: 'absolute',
      top: `${(currentPageNumber - 1) * pageHeight}px`, // Adjust based on page number
      left: '0',
      width: '100%',
      height: `${pageHeight}px`,
      backgroundColor: 'rgba(255, 255, 0, 0.3)', // Yellow highlight
      border: '5px solid yellow', // Highlight border
    };
  };

  // Log the highlighting condition
  useEffect(() => {
    console.log("Highlighted Page:", highlightedPage);
    console.log("Current Page Number:", currentPageNumber);
  }, [highlightedPage, currentPageNumber]);

  // Get file type for rendering based on extension
  const renderFileViewer = () => {
    switch (ext) {
      case 'pdf':
        return (
          <iframe
            ref={iframeRef} // Attach ref to iframe
            key={currentPageNumber}  // Force re-render when currentPageNumber changes
            src={getViewerUrl()}  // Use the generated URL
            style={{ width: '100%', height: '100vh' }}
            frameBorder="0"
            title="File Viewer"
            onLoad={handleIframeLoad}  // Call the load handler once the iframe is loaded
          />
        );
      case 'csv':
        return (
          <div style={{ height: '89vh', overflow: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {/* Assuming the CSV is passed as an array */}
                {highlightBoxes?.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c} style={{ border: '1px solid #ccc', padding: '4px 8px', fontFamily: 'monospace' }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'txt':
        return (
          <pre style={{ height: '89vh', overflow: 'auto', padding: 16, background: '#f9f9f9', border: '1px solid #ccc', borderRadius: 8, fontFamily: 'Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {highlightText || 'Loading text...'}
          </pre>
        );
      case 'docx':
      case 'doc':
        return (
          <iframe
            title="office-viewer"
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sourceUrl)}`}
            style={{ border: 0, width: '100%', height: '89vh' }}
          />
        );
      case 'xlsx':
      case 'xls':
        return (
          <iframe
            title="office-viewer"
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sourceUrl)}`}
            style={{ border: 0, width: '100%', height: '89vh' }}
          />
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'webp':
      case 'bmp':
        return (
          <div style={{ maxHeight: '89vh', overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}>
            <img src={sourceUrl} alt="Uploaded" style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />
          </div>
        );
      default:
        return <div>Unsupported file type</div>;
    }
  };

  return (
    <div className="file-viewer" style={{ position: 'relative' }}>
      {renderFileViewer()}
      {/* Overlay highlight box */}
      {highlightedPage === currentPageNumber && (
        <div style={getHighlightStyles()} />
      )}
    </div>
  );
};

export default FileViewer;
